<template>
  <div class="image-upload" :style="{'--width': width, '--height': height, '--show-upload': canUpload ? 'flex' : 'none'}" v-loading="isUploading" :element-loading-text="$t('lang.上传中')">
    <el-upload
      ref="upload"
      class="image-upload__uploader"
      :action="uploadAction"
      :disabled="disabled || uploading"
      :data="uploadData"
      :list-type="listType"
      :show-file-list="showFileList"
      :file-list="fileListInner"
      :limit="limit"
      :accept="accept"
      :before-upload="handleBeforeUpload"
      :on-change="handleFileChange"
      :on-success="handleUploadSuccess"
      :on-error="handleUploadError"
      :on-remove="handleRemove"
      :on-preview="handlePreview"
      :on-exceed="handleExceed"
      :http-request="enableCrop ? handleCustomRequest : undefined"
      :auto-upload="!enableCrop"
    >
      <slot name="trigger">
        <i class="el-icon-plus" />
      </slot>
    </el-upload>

    <!-- tip 提示放在 el-upload 外部，确保上下布局 -->
    <div v-if="tip" class="image-upload__tip-wrapper">
      <slot name="tip">
        <div class="image-upload__tip">
          {{ tip }}
        </div>
      </slot>
    </div>

    <el-dialog
      :visible.sync="cropperVisible"
      :width="dialogWidth"
      append-to-body
      class="image-upload__dialog"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      @close="handleCancelCrop"
      custom-class="image-upload__cropper-dialog"
    >
      <div slot="title">{{ $t('lang.裁剪图片') }}</div>
      <div class="image-upload__cropper-wrapper" v-if="cropperVisible && cropperImg">
        <!-- 裁剪工具栏 -->
        <div class="image-upload__cropper-toolbar">
          <el-button-group>
            <el-button type="primary" size="small" @click="rotateCropper(-90)">
              <i class="el-icon-refresh-left"></i>
            </el-button>
            <el-button type="primary" size="small" @click="rotateCropper(90)">
              <i class="el-icon-refresh-right"></i>
            </el-button>
          </el-button-group>
        </div>

        <!-- 裁剪内容区域 -->
        <div class="image-upload__cropper-content">
          <!-- 裁剪主区域 -->
          <div class="image-upload__cropper-main">
            <vue-cropper
              ref="cropper"
              :src="cropperImg"
              :aspect-ratio="mergedCropOptions.aspectRatio"
              :crop-box-resizable="mergedCropOptions.cropBoxResizable !== false"
              :guides="true"
              :view-mode="1"
              drag-mode="move"
              :auto-crop-area="0.8"
              :min-container-width="200"
              :min-container-height="200"
              :background="true"
              :rotatable="true"
              :scalable="true"
              :crop="handleCropperCropEvent"
              :cropmove="handleCropperCropMoveEvent"
              @ready="handleCropperReady"
            />
          </div>
          <!-- 预览区域 -->
          <div class="image-upload__cropper-preview" v-if="showPreview">
            <div class="image-upload__preview-title">{{ $t('lang.裁剪预览') }}</div>
            <div class="image-upload__preview-box" :style="previewStyle">
              <img v-if="previewData" :src="previewData" :style="previewStyle" />
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="image-upload__dialog-footer">
        <el-button @click="handleCancelCrop">
          {{ $t('lang.取消') }}
        </el-button>
        <el-button type="primary" @click="handleConfirmCrop" :loading="uploading">
          {{ $t('lang.确定') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 图片预览查看器 -->
    <el-image-viewer
      v-if="imageViewerVisible"
      :url-list="imageViewerUrlList"
      :initial-index="0"
      :on-close="closeImageViewer"
      :z-index="3000"
    />
  </div>
<!-- eslint-disable-next-line vue/html-indent -->
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";
import ImageViewer from "element-ui/packages/image/src/image-viewer";
import props from "./props";

// 确保正确导入组件
const CropperComponent = VueCropper.default || VueCropper;

export default {
  name: "ImageUpload",
  components: {
    VueCropper: CropperComponent,
    ElImageViewer: ImageViewer
  },
  props,
  data() {
    return {
      fileListInner: [],
      uploading: false,
      cropperVisible: false,
      cropperImg: "",
      currentFile: null,
      currentFileItem: null, // 保存当前正在处理的文件对象，用于取消时移除
      currentFileIndex: -1, // 保存当前正在处理的文件在列表中的索引，用于上传成功后替换
      isStringModel: false,
      imageViewerVisible: false, // 图片查看器显示状态
      imageViewerUrlList: [], // 预览图片URL列表
      previewData: null, // 裁剪预览数据
      previewStyle: {
        width: '200px',
        height: '200px',
        overflow: 'hidden',
        margin: '0 auto',
      },
      previewTimer: null, // 预览更新定时器
      cropMoveHandler: null, // cropmove 事件处理函数
    };
  },
  computed: {
    uploadAction() {
      return this.action || (this.$api?.file?.upload || "");
    },
    downloadAction() {
      if (this.downloadUrl) return this.downloadUrl;
      const download = this.$api?.file?.download;
      return download ? `${download}?fileCode=` : "";
    },
    canUpload() {
      // 判断是否还能上传：文件数量小于 limit
      return this.fileListInner.length < this.limit;
    },
    isUploading() {
      // 判断是否有文件正在上传：检查 fileListInner 中是否有 status 为 'uploading' 的文件
      // 或者 uploading 状态为 true（裁剪上传时）
      return this.uploading || this.fileListInner.some(file => file.status === 'uploading');
    },
    mergedCropOptions() {
      const defaultOptions = {
        aspectRatio: this.cropOptions?.fixedNumber
          ? this.cropOptions.fixedNumber[0] / this.cropOptions.fixedNumber[1]
          : undefined,
      };
      // 如果设置了 fixed: true 和 fixedNumber，禁止调整裁剪框大小
      if (this.cropOptions?.fixed && this.cropOptions?.fixedNumber) {
        defaultOptions.cropBoxResizable = false;
      }
      return Object.assign({}, defaultOptions, this.cropOptions);
    },
    showPreview() {
      // 如果指定了固定尺寸，显示预览
      return !!(this.cropOptions?.fixedNumber && this.cropOptions.fixedNumber.length === 2);
    },
    dialogWidth() {
      // 响应式弹窗宽度 - 更紧凑
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < 768) {
          return '95%'; // 小屏幕使用 95% 宽度
        } else if (width < 1024) {
          return '80%'; // 中等屏幕使用 80% 宽度
        }
      }
      return '850px'; // 大屏幕使用固定宽度，更紧凑
    },
  },
  watch: {
    value: {
      handler() {
        this.setFileListFromValue();
      },
      immediate: true,
    },
  },
  created() {
    this.isStringModel = typeof this.value === "string" || this.value === null;
  },
  methods: {
    setFileListFromValue() {
      const value = this.value;
      let list = [];
      if (typeof value === "string" && value) {
        list = value.split(",").map((code, index) => this.normalizeFile(code, index));
      } else if (Array.isArray(value) && value.length) {
        list = value.map((code, index) => this.normalizeFile(code, index));
      }
      this.fileListInner = list;
    },
    normalizeFile(code, index) {
      return {
        name: `image_${index}`,
        url: this.downloadAction ? `${this.downloadAction}${code}` : code,
        status: "success",
        response: {
          data: {
            fileCode: code,
          },
        },
      };
    },
    checkFileType(file) {
      // 检查文件类型是否符合 accept 规则
      if (!this.accept || this.accept === "image/*") {
        // 默认只检查是否是图片
        return file.type && file.type.split("/")[0] === "image";
      }

      // 解析 accept 字符串，支持多种格式：
      // "image/jpeg" - 只允许 jpeg
      // "image/jpeg,image/png" - 允许 jpeg 和 png
      // ".jpg,.png" - 允许 jpg 和 png 扩展名
      const acceptTypes = this.accept.split(",").map(type => type.trim());
      
      // 检查 MIME 类型
      if (acceptTypes.some(type => file.type === type)) {
        return true;
      }

      // 检查扩展名（如果 accept 是 .jpg, .png 格式）
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension) {
        return acceptTypes.some(type => {
          const ext = type.replace(/^\./, "").toLowerCase();
          // 支持 .jpg 或 image/jpeg 格式
          if (type.startsWith(".")) {
            return ext === fileExtension;
          }
          // 支持 image/jpeg 格式，提取扩展名
          if (type.includes("/")) {
            const mimeExt = type.split("/")[1];
            // jpeg 和 jpg 都支持
            if (mimeExt === "jpeg" && (fileExtension === "jpg" || fileExtension === "jpeg")) {
              return true;
            }
            return mimeExt === fileExtension;
          }
          return false;
        });
      }

      return false;
    },
    handleBeforeUpload(file) {
      if (this.beforeUpload && this.beforeUpload(file) === false) {
        return false;
      }

      // 验证文件类型
      if (!this.checkFileType(file)) {
        const acceptText = this.accept === "image/*" 
          ? this.$t("lang.图片")
          : this.accept;
        this.$message.error(
          this.$t("lang.请上传{0}格式文件").replace("{0}", acceptText)
        );
        return false;
      }

      if (this.maxSize && file.size > this.maxSize) {
        const sizeKB = Math.floor(this.maxSize / 1024);
        this.$message.warning(
          this.$t("lang.文件大小不能超过300KB").replace("300", sizeKB.toString())
        );
        return false;
      }

      // 如果启用裁剪，阻止自动上传，在 on-change 中处理
      if (this.enableCrop) {
        return false;
      }

      return true;
    },
    handleFileChange(file, fileList) {
      // 只在启用裁剪且文件状态为 ready 时处理
      if (!this.enableCrop) {
        return;
      }

      // 只处理新选择的文件（status 为 ready）
      if (file.status === 'ready' && file.raw) {
        // 验证文件类型
        if (!this.checkFileType(file.raw)) {
          const acceptText = this.accept === "image/*" 
            ? this.$t("lang.图片")
            : this.accept;
          this.$message.error(
            this.$t("lang.请上传{0}格式文件").replace("{0}", acceptText)
          );
          // 从文件列表中移除
          this.$refs.upload.handleRemove(file);
          return;
        }

        // 验证文件大小
        if (this.maxSize && file.raw.size > this.maxSize) {
          const sizeKB = Math.floor(this.maxSize / 1024);
          this.$message.warning(
            this.$t("lang.文件大小不能超过300KB").replace("300", sizeKB.toString())
          );
          // 从文件列表中移除
          this.$refs.upload.handleRemove(file);
          return;
        }

        this.currentFile = file.raw;
        this.currentFileItem = file; // 保存文件对象，用于取消时移除
        // 保存文件在列表中的索引，用于上传成功后替换
        this.currentFileIndex = this.fileListInner.findIndex(item => item.uid === file.uid);
        this.readFileAsDataURL(file.raw);
      }
    },
    handleCustomRequest(options) {
      // 当启用裁剪时，上传将在裁剪确认后手动触发，此方法用于占位避免控制台警告
      return options;
    },
    readFileAsDataURL(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropperImg = e.target.result;
        this.cropperVisible = true;
        // 弹窗显示后，等待 cropper 初始化完成再更新预览
        this.$nextTick(() => {
          // 延迟一下确保 cropper 完全初始化
          setTimeout(() => {
            this.updatePreview();
          }, 300);
        });
      };
      reader.onerror = () => {
        this.$message.error(this.$t("lang.读取文件失败"));
      };
      reader.readAsDataURL(file);
    },
    handleCancelCrop() {
      // 取消裁剪时，移除当前选择的文件
      if (this.currentFileItem && this.$refs.upload) {
        this.$refs.upload.handleRemove(this.currentFileItem);
      }
      this.cropperVisible = false;
      this.cropperImg = "";
      this.currentFile = null;
      this.currentFileItem = null;
      this.currentFileIndex = -1;
      this.previewData = null;
      this.stopPreviewUpdate();
      this.cropMoveHandler = null;
    },
    handleCropperReady() {
      // 裁剪器准备就绪后，更新预览
      this.$nextTick(() => {
        // 如果设置了固定尺寸，设置裁剪框的初始大小
        if (this.cropOptions?.fixed && this.cropOptions?.fixedNumber && this.$refs.cropper?.cropper) {
          const cropperInstance = this.$refs.cropper.cropper;
          const [width, height] = this.cropOptions.fixedNumber;
          // 设置裁剪框的固定大小
          cropperInstance.setCropBoxData({
            width: width,
            height: height,
          });
        }

        this.updatePreview();
        // 绑定 cropperjs 的原生事件，确保实时更新
        if (this.$refs.cropper && this.$refs.cropper.cropper) {
          const cropperInstance = this.$refs.cropper.cropper;
          // 移除之前的事件监听器（如果存在）
          if (this.cropMoveHandler) {
            cropperInstance.element.removeEventListener('cropmove', this.cropMoveHandler);
            cropperInstance.element.removeEventListener('crop', this.cropMoveHandler);
          }
          // 创建新的事件处理函数
          this.cropMoveHandler = () => {
            this.updatePreview();
            // 如果设置了固定尺寸，确保裁剪框大小不变
            if (this.cropOptions?.fixed && this.cropOptions?.fixedNumber) {
              const [width, height] = this.cropOptions.fixedNumber;
              const cropBoxData = cropperInstance.getCropBoxData();
              // 如果大小被改变，强制恢复
              if (Math.abs(cropBoxData.width - width) > 1 || Math.abs(cropBoxData.height - height) > 1) {
                cropperInstance.setCropBoxData({
                  width: width,
                  height: height,
                  left: cropBoxData.left,
                  top: cropBoxData.top,
                });
              }
            }
          };
          // 监听 cropmove 和 crop 事件实现实时预览
          // cropmove 在移动过程中触发，crop 在移动结束时也触发（双重保障）
          cropperInstance.element.addEventListener('cropmove', this.cropMoveHandler);
          cropperInstance.element.addEventListener('crop', this.cropMoveHandler);
        }
      });
    },
    handleCropperCropEvent(event) {
      // cropperjs 的 crop 事件回调（通过 props 传递）
      this.updatePreview();
    },
    handleCropperCropMoveEvent(event) {
      // cropperjs 的 cropmove 事件回调（通过 props 传递），实现实时预览
      this.updatePreview();
    },
    handleCropperCrop() {
      // Vue 事件系统的 crop 事件（备用）
      this.updatePreview();
    },
    handleCropperCropMove() {
      // Vue 事件系统的 cropmove 事件（备用）
      this.updatePreview();
    },
    updatePreview() {
      // 使用 requestAnimationFrame 实现更流畅的实时预览，而不是防抖
      if (this.previewTimer) {
        cancelAnimationFrame(this.previewTimer);
      }
      this.previewTimer = requestAnimationFrame(() => {
        if (this.$refs.cropper && this.showPreview) {
          try {
            // 确保 cropper 实例存在且已初始化
            const cropperInstance = this.$refs.cropper.cropper;
            if (!cropperInstance) {
              // 如果 cropper 还没初始化，延迟重试
              setTimeout(() => {
                this.updatePreview();
              }, 200);
              return;
            }

            const canvas = this.$refs.cropper.getCroppedCanvas({
              width: this.cropOptions?.fixedNumber?.[0] || 200,
              height: this.cropOptions?.fixedNumber?.[1] || 200,
            });
            if (canvas) {
              this.previewData = canvas.toDataURL('image/png');
              // 更新预览样式
              if (this.cropOptions?.fixedNumber) {
                const [width, height] = this.cropOptions.fixedNumber;
                const maxSize = 200;
                let previewWidth = width;
                let previewHeight = height;
                if (width > maxSize || height > maxSize) {
                  const ratio = Math.min(maxSize / width, maxSize / height);
                  previewWidth = Math.round(width * ratio);
                  previewHeight = Math.round(height * ratio);
                }
                this.previewStyle = {
                  width: `${previewWidth}px`,
                  height: `${previewHeight}px`,
                  overflow: 'hidden',
                  margin: '0 auto',
                };
              }
            }
          } catch (error) {
            console.warn('更新预览失败:', error);
          }
        }
      });
    },
    stopPreviewUpdate() {
      if (this.previewTimer) {
        cancelAnimationFrame(this.previewTimer);
        this.previewTimer = null;
      }
      // 移除事件监听器
      if (this.$refs.cropper && this.$refs.cropper.cropper && this.cropMoveHandler) {
        const cropperInstance = this.$refs.cropper.cropper;
        cropperInstance.element.removeEventListener('cropmove', this.cropMoveHandler);
        cropperInstance.element.removeEventListener('crop', this.cropMoveHandler);
        this.cropMoveHandler = null;
      }
    },
    rotateCropper(degree) {
      // 旋转图片
      if (this.$refs.cropper && this.$refs.cropper.cropper) {
        this.$refs.cropper.cropper.rotate(degree);
        // 旋转后更新预览
        this.$nextTick(() => {
          this.updatePreview();
        });
      }
    },
    handleConfirmCrop() {
      if (!this.$refs.cropper || !this.currentFile) return;
      this.uploading = true;

      const canvas = this.$refs.cropper.getCroppedCanvas({
        width: this.cropOptions?.fixedNumber?.[0] || undefined,
        height: this.cropOptions?.fixedNumber?.[1] || undefined,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      canvas.toBlob((blob) => {
        if (!blob) {
          this.uploading = false;
          // 裁剪失败时，移除临时文件
          if (this.currentFileIndex >= 0 && this.currentFileItem && this.$refs.upload) {
            this.$refs.upload.handleRemove(this.currentFileItem);
          }
          this.currentFileIndex = -1;
          this.currentFileItem = null;
          this.currentFile = null;
          this.$message.error(this.$t("lang.裁剪失败"));
          return;
        }
        const file = new File([blob], this.currentFile.name, {
          type: blob.type || "image/png"
        });

        // 将临时文件状态改为 uploading，而不是移除，避免闪烁
        // 直接更新原对象的 status 属性，保持对象引用不变
        if (this.currentFileItem && this.currentFileIndex >= 0) {
          const tempFile = this.fileListInner[this.currentFileIndex];
          if (tempFile) {
            // 直接修改 status 属性，不创建新对象，保持引用不变
            tempFile.status = 'uploading';
            // 不需要 $set，因为 tempFile 已经是响应式对象的一部分
            // 直接修改属性，Vue 会自动检测到变化
          }
        }

        this.cropperVisible = false;
        this.cropperImg = "";
        this.uploadCroppedFile(file);
      }, 'image/png', 0.9);
    },
    async uploadCroppedFile(file) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        Object.keys(this.uploadData || {}).forEach((key) => {
          formData.append(key, this.uploadData[key]);
        });

        const response = await this.$http.post(this.uploadAction, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.uploading = false;
        if (!response || response.data?.status !== 200) {
          const message = response?.data?.desc || this.$t("lang.上传失败");
          // 上传失败时，移除临时文件
          if (this.currentFileIndex >= 0 && this.currentFileItem && this.$refs.upload) {
            this.$refs.upload.handleRemove(this.currentFileItem);
          }
          this.currentFileIndex = -1;
          this.currentFileItem = null;
          this.currentFile = null;
          this.$message.error(message);
          this.onUploadError(response);
          return;
        }
        if (this.onCrop) {
          this.onCrop(response.data);
        }
        this.updateValueAfterUpload(response.data, file);
      } catch (error) {
        this.uploading = false;
        // 上传失败时，移除临时文件
        if (this.currentFileIndex >= 0 && this.currentFileItem && this.$refs.upload) {
          this.$refs.upload.handleRemove(this.currentFileItem);
        }
        this.currentFileIndex = -1;
        this.currentFileItem = null;
        this.currentFile = null;
        this.$message.error(this.$t("lang.上传失败"));
        this.onUploadError(error);
      }
    },
    handleUploadSuccess(res, file, fileList) {
      if (this.onSuccess) {
        this.onSuccess(res, file, fileList);
      }
      if (res?.status !== 200) {
        this.$message.error(res?.desc || this.$t("lang.上传失败"));
        return;
      }
      this.updateValueAfterUpload(res, file);
    },
    handleUploadError(err, file, fileList) {
      this.onUploadError(err, file, fileList);
    },
    onUploadError(err, file, fileList) {
      if (this.onError) {
        this.onError(err, file, fileList);
      }
    },
    updateValueAfterUpload(res, file) {
      const fileCode = res?.data?.fileCode;
      if (!fileCode) return;

      const newFile = {
        name: file.name || `image_${Date.now()}`,
        url: this.downloadAction ? `${this.downloadAction}${fileCode}` : fileCode,
        status: "success",
        response: res,
      };

      let newList;
      // 如果是裁剪上传，直接更新原文件对象的属性，保持引用不变，避免闪烁
      if (this.currentFileIndex >= 0 && this.currentFileIndex < this.fileListInner.length) {
        // 保存索引和引用，避免在更新过程中丢失
        const fileIndex = this.currentFileIndex;
        const oldFile = this.fileListInner[fileIndex];

        // 保存 uid，确保 el-upload 能正确识别是同一个文件
        const savedUid = oldFile.uid;

        // 直接更新原文件对象的所有属性，保持对象引用不变
        // 这样 Vue 和 el-upload 都能识别这是同一个文件，只是属性更新了
        // 同步批量更新所有属性，避免异步导致的闪烁
        Object.assign(oldFile, {
          name: newFile.name,
          url: newFile.url,
          status: newFile.status,
          response: newFile.response,
        });
        // 确保 uid 不变
        if (!oldFile.uid) {
          oldFile.uid = savedUid;
        }
        // 直接修改对象属性，Vue 的响应式系统会自动检测到变化
        // 不需要 $set，因为 oldFile 已经是响应式对象的一部分

        newList = this.fileListInner;

        // 重置索引和引用
        this.currentFileIndex = -1;
        this.currentFileItem = null;
        this.currentFile = null;
      } else {
        // 普通上传，添加新文件
        newList = [...this.fileListInner, newFile].slice(0, this.limit);
        this.fileListInner = newList;
      }

      const codes = newList.map((item) => {
        if (item?.response?.data?.fileCode) return item.response.data.fileCode;
        const { url } = item;
        if (url && this.downloadAction && url.includes(this.downloadAction)) {
          return url.replace(this.downloadAction, "");
        }
        return url;
      });

      let emitValue = codes;
      if (this.isStringModel) {
        emitValue = codes.join(",");
      }

      this.$emit("input", emitValue);
      this.$emit("change", emitValue);
    },
    handleRemove(file, fileList) {
      if (this.onRemove) {
        this.onRemove(file, fileList);
      }
      this.fileListInner = fileList;
      const codes = fileList.map((item) => {
        if (item?.response?.data?.fileCode) return item.response.data.fileCode;
        const { url } = item;
        if (url && this.downloadAction && url.includes(this.downloadAction)) {
          return url.replace(this.downloadAction, "");
        }
        return url;
      });
      let emitValue = codes;
      if (this.isStringModel) {
        emitValue = codes.join(",");
      }
      this.$emit("input", emitValue);
      this.$emit("change", emitValue);
    },
    handlePreview(file) {
      // 使用 el-image-viewer 预览图片
      const url = file.url || (file.response?.data?.fileCode ? `${this.downloadAction}${file.response.data.fileCode}` : '');
      if (url) {
        this.imageViewerUrlList = [url];
        this.imageViewerVisible = true;
      }
    },
    closeImageViewer() {
      this.imageViewerVisible = false;
      this.imageViewerUrlList = [];
    },
    handleExceed(files, fileList) {
      // 当文件数量超过 limit 时的处理
      this.$message.warning(
        this.$t("lang.最多只能上传{0}个文件").replace('{0}', this.limit.toString())
      );
    },
  },
  beforeDestroy() {
    // 清理定时器
    this.stopPreviewUpdate();
  },
};
</script>

<style scoped>
.image-upload {
  display: inline-flex;
  position: relative;
  flex-direction: column;
  row-gap: 8px;
  --width: 148px;
  --height: 148px;
}

.image-upload__uploader {
  display: inline-flex;
}

.image-upload__tip-wrapper {
  width: 100%;
  margin-top: 8px;
}

.image-upload__tip {
  font-size: 12px;
  color: orangered;
  line-height: 18px;
}

.image-upload__cropper-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 450px;
  overflow: hidden;
}

.image-upload__cropper-toolbar {
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  justify-content: flex-start;
}

.image-upload__cropper-content {
  display: flex;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

.image-upload__cropper-main {
  flex: 1;
  height: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-upload__cropper-preview {
  width: 220px;
  flex-shrink: 0;
}

.image-upload__preview-title {
  margin-bottom: 10px;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  color: #606266;
}

.image-upload__preview-box {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.image-upload__preview-box img {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.image-upload__dialog-footer {
  display: flex;
  column-gap: 12px;
  justify-content: flex-end;
}

::v-deep .el-upload--picture-card {
  width: var(--width);
  height: var(--height);
  display: var(--show-upload, inline-flex);
  align-items: center;
  justify-content: center;
}

::v-deep .el-upload-list--picture-card .el-upload-list__item {
  width: var(--width);
  height: var(--height);
  /* 禁用过渡动画，避免更新时闪烁 */
  transition: none !important;
  -webkit-transition: none !important;
}

::v-deep .image-upload__cropper-main .cropper-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

::v-deep .image-upload__cropper-main .cropper-view-box,
::v-deep .image-upload__cropper-main .cropper-face {
  border-radius: 0;
}

::v-deep .image-upload__cropper-dialog {
  margin: 0 auto;
  margin-top: 10vh !important;
}

::v-deep .image-upload__cropper-dialog .el-dialog {
  overflow: hidden !important;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

::v-deep .image-upload__cropper-dialog .el-dialog__header {
  padding: 10px 12px;
  flex-shrink: 0;
  border-bottom: 1px solid #e4e7ed;
}

::v-deep .image-upload__cropper-dialog .el-dialog__body {
  padding: 10px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(80vh - 100px);
}

::v-deep .image-upload__cropper-dialog .el-dialog__footer {
  padding: 6px 12px;
  flex-shrink: 0;
  border-top: 1px solid #e4e7ed;
}

::v-deep .image-upload__dialog .el-dialog__body {
  padding: 20px;
  overflow: visible;
}

::v-deep .image-upload__dialog .el-dialog {
  overflow: visible;
}

/* 小屏幕适配 */
@media (max-width: 768px) {
  ::v-deep .image-upload__cropper-dialog {
    width: 95% !important;
    margin-top: 5vh !important;
  }

  .image-upload__cropper-wrapper {
    height: calc(100vh - 200px);
    max-height: 400px;
  }

  .image-upload__cropper-content {
    flex-direction: column;
  }

  .image-upload__cropper-main {
    height: 300px;
  }

  .image-upload__cropper-preview {
    width: 100%;
    margin-top: 16px;
  }

  ::v-deep .image-upload__cropper-dialog .el-dialog {
    max-height: 90vh;
  }

  ::v-deep .image-upload__cropper-dialog .el-dialog__body {
    padding: 8px;
    max-height: calc(90vh - 100px);
  }

  ::v-deep .image-upload__cropper-dialog .el-dialog__header {
    padding: 8px 10px;
  }

  ::v-deep .image-upload__cropper-dialog .el-dialog__footer {
    padding: 5px 10px;
  }
}

</style>

